import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  where,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type { ActionItem, ActionItemStatus, DayFocus, DayFocusPriority, TaskPlanStatus, TodoSwimLane } from '../types';
import { isValidDateKey } from '../utils/todoSwimLanes';

const paths = {
  dayFocuses: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'dayFocus'),
  dayFocus: (userId: string, focusId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'dayFocus', focusId),
};

function getActionItemStatus(item: ActionItem): ActionItemStatus {
  if (item.status) return item.status;
  return item.completed ? 'done' : 'todo';
}

function isActionItemDone(item: ActionItem): boolean {
  return getActionItemStatus(item) === 'done';
}

export function getTodayDateKey(date = new Date()): string {
  const local = new Date(date);
  const year = local.getFullYear();
  const month = String(local.getMonth() + 1).padStart(2, '0');
  const day = String(local.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export async function createDayFocus(
  userId: string,
  sessionId: string,
  title: string,
  summary: string | undefined,
  priority: DayFocusPriority,
  actionItems: Omit<ActionItem, 'completed' | 'completedAt'>[],
  focusDate: string = getTodayDateKey()
): Promise<string> {
  const now = Timestamp.now();
  const items: ActionItem[] = actionItems.map((item) => ({
    ...item,
    completed: false,
    status: 'todo',
  }));

  const focusData: Omit<DayFocus, 'id'> = {
    userId,
    sessionId,
    title,
    summary: summary?.trim() || '',
    priority,
    status: 'active',
    actionItems: items,
    focusDate,
    createdAt: now,
    updatedAt: now,
  };

  const ref = await addDoc(paths.dayFocuses(userId), focusData);
  return ref.id;
}

export async function getDayFocus(userId: string, focusId: string): Promise<DayFocus | null> {
  const snap = await getDoc(paths.dayFocus(userId, focusId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as DayFocus;
}

export async function getAllDayFocus(userId: string): Promise<DayFocus[]> {
  const q = query(paths.dayFocuses(userId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as DayFocus));
}

export async function getDayFocusForDate(userId: string, dateKey: string): Promise<DayFocus[]> {
  const q = query(
    paths.dayFocuses(userId),
    where('focusDate', '==', dateKey),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as DayFocus));
}

export async function toggleDayFocusItem(
  userId: string,
  focusId: string,
  actionItemId: string,
  completed: boolean
): Promise<void> {
  const focus = await getDayFocus(userId, focusId);
  if (!focus) return;
  const now = Timestamp.now();

  const updatedItems = focus.actionItems.map((item) => {
    if (item.id !== actionItemId) return item;
    const { completedAt: _completedAt, ...rest } = item;
    if (completed) {
      const next: ActionItem = { ...rest, status: 'done', completed: true, completedAt: now };
      return next;
    }
    const next: ActionItem = { ...rest, status: 'todo', completed: false };
    return next;
  });

  const allComplete = updatedItems.length > 0 && updatedItems.every((item) => isActionItemDone(item));
  const updates: Record<string, unknown> = {
    actionItems: updatedItems,
    updatedAt: Timestamp.now(),
  };

  if (allComplete) {
    updates.status = 'completed' satisfies TaskPlanStatus;
    updates.completedAt = Timestamp.now();
  } else if (focus.status === 'completed') {
    updates.status = 'active' satisfies TaskPlanStatus;
    updates.completedAt = null;
  }

  await updateDoc(paths.dayFocus(userId, focusId), updates);
}

export async function addDayFocusItem(
  userId: string,
  focusId: string,
  text: string,
  options?: { parentItemId?: string | null; afterItemId?: string }
): Promise<string | null> {
  const focus = await getDayFocus(userId, focusId);
  if (!focus) return null;

  const maxOrder = focus.actionItems.reduce((max, item) => Math.max(max, item.order), -1);
  const newItem: ActionItem = {
    id: `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text,
    completed: false,
    status: 'todo',
    order: maxOrder + 1,
    ...(options?.parentItemId ? { parentItemId: options.parentItemId } : {}),
  };

  let nextItems: ActionItem[];
  if (options?.afterItemId) {
    const idx = focus.actionItems.findIndex((it) => it.id === options.afterItemId);
    if (idx >= 0) {
      nextItems = [
        ...focus.actionItems.slice(0, idx + 1),
        newItem,
        ...focus.actionItems.slice(idx + 1),
      ].map((it, i) => ({ ...it, order: i }));
    } else {
      nextItems = [...focus.actionItems, newItem];
    }
  } else {
    nextItems = [...focus.actionItems, newItem];
  }

  await updateDoc(paths.dayFocus(userId, focusId), {
    actionItems: nextItems,
    updatedAt: Timestamp.now(),
  });
  return newItem.id;
}

export async function deleteDayFocusItem(
  userId: string,
  focusId: string,
  actionItemId: string
): Promise<void> {
  const focus = await getDayFocus(userId, focusId);
  if (!focus) return;

  const filtered = focus.actionItems.filter((item) => item.id !== actionItemId);
  const updates: Record<string, unknown> = {
    actionItems: filtered,
    updatedAt: Timestamp.now(),
  };

  const allComplete = filtered.length > 0 && filtered.every((item) => isActionItemDone(item));
  if (allComplete) {
    updates.status = 'completed' satisfies TaskPlanStatus;
    updates.completedAt = Timestamp.now();
  }

  await updateDoc(paths.dayFocus(userId, focusId), updates);
}

/** Rename an action item on a `DayFocus`. Empty trimmed text deletes it. */
export async function renameDayFocusItem(
  userId: string,
  focusId: string,
  actionItemId: string,
  text: string
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) {
    await deleteDayFocusItem(userId, focusId, actionItemId);
    return;
  }
  const focus = await getDayFocus(userId, focusId);
  if (!focus) return;
  const next = focus.actionItems.map((item) =>
    item.id === actionItemId ? { ...item, text: trimmed } : item
  );
  await updateDoc(paths.dayFocus(userId, focusId), {
    actionItems: next,
    updatedAt: Timestamp.now(),
  });
}

export async function reorderDayFocusItems(
  userId: string,
  focusId: string,
  orderedItemIds: string[],
  statusByItemId?: Partial<Record<string, ActionItemStatus>>,
  parentByItemId?: Partial<Record<string, string | null>>,
): Promise<void> {
  const focus = await getDayFocus(userId, focusId);
  if (!focus) return;

  const itemMap = new Map(focus.actionItems.map((item) => [item.id, item]));
  const now = Timestamp.now();
  const reordered = orderedItemIds
    .map((id, index) => {
      const item = itemMap.get(id);
      if (!item) return null;
      const { completedAt: _completedAt, parentItemId: prevParent, ...rest } = item;
      const nextStatus = statusByItemId?.[id] ?? getActionItemStatus(item);

      let nextParent: string | undefined = prevParent;
      if (parentByItemId && Object.prototype.hasOwnProperty.call(parentByItemId, id)) {
        const patch = parentByItemId[id];
        nextParent = patch === null || patch === undefined ? undefined : patch;
      }
      const parentField = nextParent ? { parentItemId: nextParent } : {};

      const isDone = nextStatus === 'done';
      if (isDone) {
        const next: ActionItem = {
          ...rest,
          ...parentField,
          order: index,
          status: nextStatus,
          completed: true,
          completedAt: item.completedAt ?? now,
        };
        return next;
      }
      const next: ActionItem = {
        ...rest,
        ...parentField,
        order: index,
        status: nextStatus,
        completed: false,
      };
      return next;
    })
    .filter((item): item is ActionItem => item !== null);

  const allComplete = reordered.length > 0 && reordered.every((item) => isActionItemDone(item));
  const updates: Record<string, unknown> = {
    actionItems: reordered,
    updatedAt: Timestamp.now(),
  };
  if (allComplete) {
    updates.status = 'completed' satisfies TaskPlanStatus;
    updates.completedAt = now;
  } else if (focus.status === 'completed') {
    updates.status = 'active' satisfies TaskPlanStatus;
    updates.completedAt = null;
  }

  await updateDoc(paths.dayFocus(userId, focusId), updates);
}

export async function updateDayFocusStatus(
  userId: string,
  focusId: string,
  status: TaskPlanStatus
): Promise<void> {
  const updates: Record<string, unknown> = {
    status,
    updatedAt: Timestamp.now(),
  };
  if (status === 'completed') updates.completedAt = Timestamp.now();
  await updateDoc(paths.dayFocus(userId, focusId), updates);
}

export async function deleteDayFocus(userId: string, focusId: string): Promise<void> {
  await deleteDoc(paths.dayFocus(userId, focusId));
}

/** Rename a `DayFocus` title. Used by the bullets outline view for inline edits on the top-level row. */
export async function updateDayFocusTitle(
  userId: string,
  focusId: string,
  title: string,
): Promise<void> {
  const trimmed = title.trim();
  if (!trimmed) return;
  await updateDoc(paths.dayFocus(userId, focusId), {
    title: trimmed,
    updatedAt: Timestamp.now(),
  });
}

/** Set/clear an item's `parentItemId` inside a `DayFocus`. Mirrors `setActionItemParent` on tasks. */
export async function setDayFocusItemParent(
  userId: string,
  focusId: string,
  actionItemId: string,
  parentItemId: string | null,
): Promise<void> {
  if (parentItemId === actionItemId) return;
  const focus = await getDayFocus(userId, focusId);
  if (!focus) return;

  if (parentItemId) {
    const byId = new Map(focus.actionItems.map((it) => [it.id, it] as const));
    let cursor: string | undefined = parentItemId;
    const seen = new Set<string>();
    while (cursor) {
      if (cursor === actionItemId) return;
      if (seen.has(cursor)) break;
      seen.add(cursor);
      cursor = byId.get(cursor)?.parentItemId;
    }
  }

  const next = focus.actionItems.map((item) => {
    if (item.id !== actionItemId) return item;
    if (parentItemId === null || parentItemId === undefined) {
      const { parentItemId: _drop, ...rest } = item;
      return rest;
    }
    return { ...item, parentItemId };
  });

  await updateDoc(paths.dayFocus(userId, focusId), {
    actionItems: next,
    updatedAt: Timestamp.now(),
  });
}

export async function updateDayFocusDate(
  userId: string,
  focusId: string,
  newDate: string
): Promise<void> {
  await updateDoc(paths.dayFocus(userId, focusId), {
    focusDate: newDate,
    updatedAt: Timestamp.now(),
  });
}

export async function updateDayFocusSwimLane(
  userId: string,
  focusId: string,
  swimLane: TodoSwimLane | null
): Promise<void> {
  await updateDoc(paths.dayFocus(userId, focusId), {
    swimLane: swimLane ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/** Assign or clear the project on a `DayFocus`. See PRD §10 for policy. */
export async function updateDayFocusProject(
  userId: string,
  focusId: string,
  projectId: string | null
): Promise<void> {
  await updateDoc(paths.dayFocus(userId, focusId), {
    projectId: projectId ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/** Set or clear `dueDate` on a `DayFocus`. */
export async function updateDayFocusDueDate(
  userId: string,
  focusId: string,
  dueDate: string | null
): Promise<void> {
  if (dueDate !== null && !isValidDateKey(dueDate)) {
    throw new Error('dueDate must be in YYYY-MM-DD format.');
  }
  await updateDoc(paths.dayFocus(userId, focusId), {
    dueDate: dueDate ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}
